// Piano comping: conventional candidates, hand-aware phrase optimization,
// and duration-aware melody compatibility. See docs/piano-voicing-review.md.

import { instrumentById, type InstrumentVoicing } from './instruments';
import {
  hasFlatNineClashMidis,
  isMuddy,
  LH_REGISTER,
  PIANO_FAMILIES,
  type PianoFamily,
} from './pianoVoicings';
import { pianoMovement, pianoPhrase } from './pianoVoiceLeading';
import {
  enumerateLeftHand,
  isLeftHandFamily,
  LH_ANCHOR,
  type PianoLeftHandFamily,
} from './pianoLeftHand';
import { TICKS_PER_BEAT, type ChartMelodyNote } from './chartMelody';
import { CHORD_TYPES, chordOwnsFlatNine, chordSymbol, type ChordTypeId } from './chords';
import { spellVoicingMidis } from './chordSpelling';
import { pitchClassOf, sharpName } from './notes';
import type { Inversion } from './voicing';

const mod12 = (n: number) => ((n % 12) + 12) % 12;

const ROTATIONS: Inversion[] = [0, 1, 2, 3];

/**
 * The families the workbench's comp channel may pick (#236). A strict subset
 * of `PIANO_FAMILIES`, chosen by one hard rule: a comp family must answer for
 * EVERY root × catalogue type under this module's default options, because
 * `voiceLeadPiano` is all-or-null — one unvoiceable chord and the whole tune
 * silently falls back to guitar, which reads as the picker being broken.
 * Probed over the full catalogue: `guide-tones`, `guide-tones-root`,
 * `shell`, `two-hand`,
 * `upper-structure` and `rootless` are natively total; `close` misses only
 * dom13 (its written stack puts the 13 a true minor ninth under the ♭7) and
 * `quartal` misses the four fourthless types — those gaps are pinned by name
 * in the test, and for exactly those chords the walk answers two-hand
 * instead (see `candidatesFor`). `drop-2` needs a wider-than-hand span, so
 * it stays in the Lab.
 *
 * Ordered as the pedagogical ladder the methods teach: two notes → three →
 * the full textures.
 */
export type PianoCompFamily =
  | 'guide-tones'
  | 'guide-tones-root'
  | 'shell'
  | 'close'
  | 'two-hand'
  | 'rootless'
  | 'upper-structure'
  | 'quartal'
  | 'mantooth'
  | PianoLeftHandFamily;
/** The two-handed piano's families — what `PIANO_COMP_FAMILIES` lists; the left
 * hand's are `PIANO_LEFT_HAND_FAMILIES` (pianoLeftHand.ts). */
export type PianoTwoHandFamily = Exclude<PianoCompFamily, PianoLeftHandFamily>;

export const PIANO_COMP_FAMILIES: { id: PianoTwoHandFamily; label: string; hint: string }[] = [
  {
    id: 'mantooth',
    label: 'Mantooth (two hands)',
    hint: 'Generic, Miracle and polychord voicings — RH thumb near middle C, compact space between hands',
  },
  {
    id: 'guide-tones',
    label: 'Guide tones (3 & 7)',
    hint: '3rd & 7th, or 3rd & 5th on triads — two continuous voices; bass supplies the root',
  },
  {
    id: 'guide-tones-root',
    label: 'Guide tones + root',
    hint: 'LH root under the 3rd & 7th — the duo texture when nobody states the root',
  },
  {
    id: 'shell',
    label: 'Shells (R-3-7)',
    hint: 'Root, 3rd and 7th in compact inversions — extensions omitted',
  },
  {
    id: 'close',
    label: 'Close position',
    hint: 'Four-way close — every chord tone under one hand',
  },
  {
    id: 'two-hand',
    label: 'Two hands',
    hint: 'LH root under a rootless RH stack — solo-piano comping',
  },
  {
    id: 'rootless',
    label: 'Rootless',
    hint: 'Evans/Kelly A & B forms — the bass player has the root',
  },
  {
    id: 'upper-structure',
    label: 'Upper structures',
    hint: 'A tension triad in the RH over an LH shell — Levine’s two-hand texture',
  },
  {
    id: 'quartal',
    label: 'Quartal (fourths)',
    hint: 'Stacks of fourths from chord tones and tensions, with a tritone LH on dominants',
  },
];

/** The chord fields the piano search needs — a structural subset of
 * `SequenceChord`, so any sequence the guitar optimiser accepts works here. */
export interface PianoCompChord {
  root: string;
  chordType: ChordTypeId;
  /** The root AS WRITTEN, when the caller has it. Spelling follows this, so a
   * flat-key tune's voicings read in flats instead of the engine's internal
   * sharp names. */
  displayRoot?: string;
  /** Melody pitches sounding while this chord rings. The walk steers around
   * them: a voicing that parks a tone a semitone off the sung melody grinds
   * for the chord's whole duration on a sustaining piano (found on Stella By
   * Starlight, whose m7♭5-heavy head rubbed 15 of 77 melody notes before the
   * walk knew the melody existed). */
  avoidMidis?: number[];
  /** Fraction of this chord occupied by each melody pitch (overlap union). */
  melodyExposure?: { midi: number; weight: number }[];
  /** The slash bass as written ("C" in "D/C"), when the symbol names one. */
  bass?: string;
  /** The user's own stack for this chord (voicingPins.ts, already moved to
   * the chord's current root): the walk's whole candidate layer, so the
   * neighbours voice-lead into and out of it. */
  pinnedVoicing?: PinnedPianoVoicing;
}

export interface PinnedPianoVoicing {
  midis: number[];
  hands?: { left: number[]; right: number[] };
  family?: string;
  inversion?: number;
}

/** A pinned stack as the walk's candidate: names spelled against the chord
 * as written, the pin's own family and rotation for the readout. */
export function pinnedInstrumentVoicing(
  chord: PianoCompChord,
  fallbackFamily: string,
): InstrumentVoicing {
  const pin = chord.pinnedVoicing!;
  const displayRoot = chord.displayRoot ?? chord.root;
  return {
    midis: pin.midis,
    voicing: spellVoicingMidis(pin.midis, displayRoot, chord.chordType, sharpName),
    inversion: (pin.inversion ?? 0) as Inversion,
    family: pin.family ?? fallbackFamily,
    symbol: chordSymbol(displayRoot, chord.chordType),
    ...(pin.hands ? { hands: pin.hands } : {}),
  };
}

export type PianoVoicedChord<T> = T & {
  /** Sounding pitches, low→high. */
  midis: number[];
  /** Reserved for explicitly authored revoicing; the comp search keeps its
   * chosen voicing on every attack instead of adding unplanned inversions. */
  liftMidis?: number[];
  /** Diatonic note names for `midis`, low→high (see chordSpelling.ts). */
  voicing: string[];
  /** Which pitches each hand takes (two-hand / upper-structure / quartal). */
  hands?: { left: number[]; right: number[] };
  /** Which triad the right hand plays (upper-structure family only). */
  upperTriad?: { root: string; quality: 'major' | 'minor' };
  /** Which family actually voiced this chord. Usually the picked one; a
   * chord outside a comp family's vocabulary (a dim7 under quartal) answers
   * `two-hand` instead — see `candidatesFor` — and this says so. */
  family: PianoFamily | PianoLeftHandFamily;
  inversion: Inversion;
  /** Hand-aware semitone movement, plus 3 per entering/leaving voice
   * (undefined for the first chord). No expressive leap weighting. */
  distance?: number;
};

export interface PianoCompOptions {
  family?: PianoFamily | PianoLeftHandFamily;
  /** Register the right hand (and one-hand families) must sit in. */
  register?: { low: number; high: number };
  maxHandSpan?: number;
  avoidB9?: boolean;
  /** Preferred bottom of the comping hand (default G3). A soft preference,
   * allowing the phrase to start where subsequent resolutions have room.
   * Two-hand families apply it to the RH; their LH keeps its bass register. */
  anchorMidi?: number;
}

const DEFAULT_ANCHOR = 55; // G3: room below the opening for downward resolutions.

/** Sustained rubs matter; repeated passing notes must not multiply the bill.
 * Essential guide tones have less freedom than added tensions, so continuity
 * takes precedence there. These are implementation weights, not textbook rules. */
function melodyRubPenalty(midis: number[], chord: PianoCompChord, family: string): number {
  const exposure =
    chord.melodyExposure ??
    [...new Set(chord.avoidMidis ?? [])].map((midi) => ({ midi, weight: 1 }));
  let penalty = 0;
  for (const midi of midis) {
    for (const heard of exposure) {
      const distance = Math.abs(midi - heard.midi);
      penalty += (distance === 1 ? 12 : distance === 13 ? 4 : 0) * heard.weight;
    }
  }
  return (
    Math.min(12, penalty) * (family === 'guide-tones' || family === 'guide-tones-root' ? 0.25 : 1)
  );
}

/**
 * The duration of each melody pitch during a chord — the `melodyExposure`
 * input to `voiceLeadPiano`, computed from the same flattened measures and
 * bar starts the chord sequence came from. Pure so the hook and the tests
 * share one definition of "sounding during".
 */
export function melodyExposureByChord(
  chords: { barIndex: number; beat: number; durationBeats: number }[],
  measures: { melody?: ChartMelodyNote[] }[],
  barStartBeats: number[],
): ({ midi: number; weight: number }[] | undefined)[] {
  const notes: { start: number; end: number; midi: number }[] = [];
  measures.forEach((measure, index) => {
    const base = barStartBeats[index] ?? 0;
    for (const note of measure.melody ?? []) {
      if (note.rest || note.midi == null) continue;
      const start = base + note.startTicks / TICKS_PER_BEAT;
      notes.push({ start, end: start + note.durationTicks / TICKS_PER_BEAT, midi: note.midi });
    }
  });
  return chords.map((chord) => {
    const start = (barStartBeats[chord.barIndex] ?? 0) + chord.beat - 1;
    const end = start + chord.durationBeats;
    const spans = new Map<number, [number, number][]>();
    for (const note of notes) {
      const lo = Math.max(start, note.start);
      const hi = Math.min(end, note.end);
      if (hi <= lo + 1e-3) continue;
      const list = spans.get(note.midi) ?? [];
      list.push([lo, hi]);
      spans.set(note.midi, list);
    }
    const exposure = [...spans].map(([midi, intervals]) => {
      intervals.sort((a, b) => a[0] - b[0]);
      let covered = 0;
      let until = start;
      for (const [lo, hi] of intervals) {
        covered += Math.max(0, hi - Math.max(lo, until));
        until = Math.max(until, hi);
      }
      return { midi, weight: covered / chord.durationBeats };
    });
    return exposure.length ? exposure : undefined;
  });
}

/** Compatibility helper for consumers interested only in sounding pitches. */
export function melodyAvoidMidis(
  chords: { barIndex: number; beat: number; durationBeats: number }[],
  measures: { melody?: ChartMelodyNote[] }[],
  barStartBeats: number[],
): (number[] | undefined)[] {
  return melodyExposureByChord(chords, measures, barStartBeats).map((exposure) =>
    exposure?.map((note) => note.midi),
  );
}

/** Exported for the chord-melody walk (pianoMelody.ts), whose chart-only
 * spans fall back to exactly these comping candidates — one definition of
 * "the piano candidates for a chord", not two. */
/**
 * A SLASH CHORD'S LEFT HAND PLAYS THE NAMED BASS. A hybrid is "this structure
 * over that bass" (the reharm ladder's rung 7), and on a piano the left hand
 * is what states the bottom — so where a voicing divides between the hands and
 * the LEFT is stating the root, the root gives way to the slash note and the
 * right hand, which carries the chord, is untouched.
 *
 * Deliberately narrow. A family whose LH carries STRUCTURE rather than the
 * root — upper-structure's R-3-7 shell, quartal's tritone anchor — would lose
 * its guide tones if the bass took that hand, so those are left alone and the
 * bass player states the slash, which is also what happens on the one-hand
 * families (a guide-tone dyad never stated a root to begin with). The gates
 * run again on the result. Prefer clash-free placements, but if none exist,
 * retain an explicitly written slash bass against an essential chord tone.
 * This exception never licenses internal RH clashes or muddy spacing.
 */
function overSlashBass(
  voicings: InstrumentVoicing[],
  chord: PianoCompChord,
  options: PianoCompOptions,
): InstrumentVoicing[] {
  if (!chord.bass) return voicings;
  const bassPc = pitchClassOf(chord.bass);
  const rootPc = pitchClassOf(chord.root);
  if (bassPc === rootPc) return voicings;
  const avoidB9 = options.avoidB9 ?? true;
  const out: InstrumentVoicing[] = [];
  const writtenClashes: InstrumentVoicing[] = [];
  const chordPcs = new Set(CHORD_TYPES[chord.chordType].intervals.map((iv) => mod12(rootPc + iv)));
  for (const voicing of voicings) {
    const hands = voicing.hands;
    // Mantooth's LH is part of the coupled harmonic shape, including its
    // suspended root/fifth pair; the ensemble bassist supplies the slash.
    if (voicing.family === 'mantooth') {
      out.push(voicing);
      continue;
    }
    // Only where the left hand is the root (with or without its fifth).
    if (!hands || !hands.left.every((m) => mod12(m) === rootPc || mod12(m) === mod12(rootPc + 7))) {
      out.push(voicing);
      continue;
    }
    const right = hands.right;
    for (
      let bass = LH_REGISTER.low + mod12(bassPc - LH_REGISTER.low);
      bass <= LH_REGISTER.high;
      bass += 12
    ) {
      if (bass >= right[0]) continue;
      const midis = [bass, ...right];
      const clashes =
        avoidB9 && !chordOwnsFlatNine(chord.chordType) && hasFlatNineClashMidis(midis);
      if (
        clashes &&
        (hasFlatNineClashMidis(right) ||
          right.some((m) => m - bass >= 13 && mod12(m - bass) === 1 && !chordPcs.has(mod12(m))))
      )
        continue;
      if (isMuddy(midis)) continue;
      (clashes ? writtenClashes : out).push({
        ...voicing,
        midis,
        voicing: spellVoicingMidis(
          midis,
          chord.displayRoot ?? chord.root,
          chord.chordType,
          sharpName,
        ),
        hands: { left: [bass], right },
      });
    }
  }
  return out.length ? out : writtenClashes;
}

export function candidatesForCompChord(
  chord: PianoCompChord,
  options: PianoCompOptions,
): InstrumentVoicing[] {
  const piano = instrumentById('piano');
  const family = options.family ?? 'two-hand';
  if (isLeftHandFamily(family)) return leftHandCandidates(chord, family, options);
  const enumerate = (of: PianoFamily) =>
    ROTATIONS.flatMap((rotation) =>
      piano.enumerate(chord.root, chord.chordType, rotation, of, {
        register: options.register,
        maxHandSpan: options.maxHandSpan,
        avoidB9: options.avoidB9,
        displayRoot: chord.displayRoot ?? chord.root,
      }),
    );
  const own = overSlashBass(enumerate(family), chord, options);
  if (own.length) return own;
  // A COMP family may have a chord outside its vocabulary (quartal has no
  // answer for the four fourthless types — pinned by name in the test). The
  // comp walk must not play a hole and must not drop the whole tune back to
  // guitar over one bar, so exactly those chords answer two-hand — which is
  // what a pianist does when the idiom runs out. Lab-only families keep the
  // honest null: this rescue is part of what ADMITS a family to the picker,
  // not a way around the admission rule.
  if (!PIANO_COMP_FAMILIES.some((f) => f.id === family)) return own;
  const twoHand =
    family === 'two-hand' ? own : overSlashBass(enumerate('two-hand'), chord, options);
  if (twoHand.length) return twoHand;
  // Two-hand is total on its own, so an empty answer is a SLASH it cannot
  // take: a bass a half step above the root (D∆/E♭, E7/F) or on a dominant's
  // ♭6 (D7/B♭) sits a true minor ninth under the rootless stack's added 9th or
  // 13th in every placement, and an added tension never earns that clash
  // (overSlashBass). The chord's own close stack carries no added tension —
  // D F♯ A C♯ over E♭ is the textbook D∆/E♭ — so it answers, the bassist
  // stating the slash as on every one-hand family; dom13, close's one gap,
  // takes the shell of its seventh, which carries no added tension either.
  // Before this, 21 Jazz 1460 charts voiced null with the default family and
  // the whole tune fell back to guitar.
  const close = enumerate('close');
  return close.length ? close : enumerate('shell');
}

/** The highest note the left hand may reach under a chord: one below the
 * lowest melody note sounding in it, or no limit without a melody. */
function leftHandCeiling(chord: PianoCompChord): number | undefined {
  const melody = chord.melodyExposure?.map((e) => e.midi) ?? chord.avoidMidis ?? [];
  return melody.length ? Math.min(...melody) - 1 : undefined;
}

/**
 * The LEFT-HAND families (pianoLeftHand.ts): one hand under the melody. The
 * hand stays BELOW the lowest melody note sounding in the chord (the
 * `ceiling`); a slash bass is the bassist's; and the rescue for a chord the
 * family cannot voice is the next left-hand texture down the list — shells,
 * then close position — so the comp never grows a right hand it was asked
 * not to have. (Every catalogue type has a shell or a close form, pinned.)
 */
function leftHandCandidates(
  chord: PianoCompChord,
  family: PianoLeftHandFamily,
  options: PianoCompOptions,
): InstrumentVoicing[] {
  const lhOptions = {
    avoidB9: options.avoidB9,
    displayRoot: chord.displayRoot ?? chord.root,
    ceiling: leftHandCeiling(chord),
  };
  const toInstrument = (voicings: ReturnType<typeof enumerateLeftHand>): InstrumentVoicing[] =>
    voicings.map((v) => ({
      midis: v.midis,
      voicing: v.voicing,
      inversion: v.inversion,
      family: v.family,
      symbol: chordSymbol(chord.displayRoot ?? chord.root, chord.chordType),
      span: v.span,
    }));
  for (const tried of [family, 'lh-shell', 'lh-close'] as PianoLeftHandFamily[]) {
    const own = toInstrument(enumerateLeftHand(chord.root, chord.chordType, tried, lhOptions));
    if (own.length) return own;
  }
  return [];
}

/** Global phrase search: retain common tones, avoid large individual leaps,
 * and keep each hand in its role. Broad physical ranges remain available, but
 * a soft register cost discourages parking the accompaniment above the solo. */
export function voiceLeadPiano<T extends PianoCompChord>(
  sequence: T[],
  options: PianoCompOptions = {},
): PianoVoicedChord<T>[] | null {
  const family = options.family ?? 'two-hand';
  if (!(PIANO_FAMILIES as readonly string[]).includes(family) && !isLeftHandFamily(family)) {
    return null;
  }
  const anchor =
    options.anchorMidi ??
    (family === 'mantooth' ? 64 : isLeftHandFamily(family) ? LH_ANCHOR : DEFAULT_ANCHOR);
  const cache = new Map<string, InstrumentVoicing[]>();
  const layers = sequence.map((chord) => {
    if (chord.pinnedVoicing) return [pinnedInstrumentVoicing(chord, family)];
    // A left-hand family's candidates depend on the melody too (the ceiling),
    // so a repeated chord under a different melody note is a different layer.
    const key = JSON.stringify([
      chord.root,
      chord.chordType,
      chord.bass,
      chord.displayRoot,
      isLeftHandFamily(family) ? (leftHandCeiling(chord) ?? null) : null,
    ]);
    let candidates = cache.get(key);
    if (!candidates) {
      candidates = [
        ...new Map(
          candidatesForCompChord(chord, options).map((c) => [
            JSON.stringify([c.midis, c.hands]),
            c,
          ]),
        ).values(),
      ];
      cache.set(key, candidates);
    }
    return candidates;
  });
  const chosen = pianoPhrase(
    layers,
    (candidate, index) => {
      const upper = candidate.hands?.right ?? candidate.midis;
      // The anchor describes the comping hand. Root-bearing LH has its own home.
      const register =
        Math.abs(upper[0] - anchor) * 0.2 +
        Math.max(0, upper[upper.length - 1] - (anchor + 17)) ** 2 * 0.2;
      const opening = index === 0 ? Math.abs(upper[0] - anchor) * 0.5 : 0;
      const bass = candidate.hands ? Math.abs(candidate.hands.left[0] - 48) * 0.08 : 0;
      // Mantooth's below-C4 exception is available for continuity, but a
      // normal thumb placement takes priority over small movement savings.
      const thumb = family === 'mantooth' ? Math.max(0, 60 - upper[0]) ** 2 * 2 : 0;
      return (
        register +
        opening +
        bass +
        thumb +
        melodyRubPenalty(candidate.midis, sequence[index], family)
      );
    },
    (a, b) => pianoMovement(a, b, true),
  );
  if (!chosen) return null;
  return chosen.map((candidate, index) => ({
    ...sequence[index],
    midis: candidate.midis,
    liftMidis: undefined,
    voicing: candidate.voicing,
    hands: candidate.hands,
    upperTriad: candidate.upperTriad,
    family: candidate.family as PianoFamily | PianoLeftHandFamily,
    inversion: candidate.inversion,
    ...(index ? { distance: pianoMovement(chosen[index - 1], candidate) } : {}),
  }));
}

/** Total pitch-space movement of a voiced line — the piano's `lineCost`. */
export function pianoLineCost(voiced: PianoVoicedChord<unknown>[]): number {
  return voiced.reduce((sum, chord) => sum + (chord.distance ?? 0), 0);
}
